/*
  Ячейка «Должник» реестра (решение владельца 19.10.2026, макет
  «3-реестр-должник-А»): фамилия с инициалами, при нескольких - «и ещё N»,
  у нанимателя серая метка.

  Все слова приходят с сервера готовыми (`AccountPeopleOut`): кто стоит первым,
  решает то же правило, что ставит имя в шапку выписки для суда, и второго его
  определения здесь быть не должно (скилл `frontend`, правило 5).

  Перечень под «и ещё N» открывается наведением и фокусом с клавиатуры, а на
  телефоне - нажатием: курсора там нет, и без нажатия перечень был бы виден
  только на широком экране. Закрывается уходом курсора, потерей фокуса (нажатие
  в другом месте) и клавишей Escape.

  Панель - `position: fixed` и встаёт по `placeHint`, как пояснение значка «i»:
  таблица реестра лежит в обёртке с прокруткой, и панель внутри ячейки была бы
  обрезана её краем.
*/

import { useLayoutEffect, useRef, useState } from 'react'

import { Tag } from './Common.jsx'
import { placeHint } from './hintPlacement.js'

// Первый шаг показа: панель есть, но не видна, пока её не замерили.
const MEASURING = { position: 'fixed', left: 0, top: 0, visibility: 'hidden' }

export default function RegistryDebtor({ debtor }) {
  if (!debtor) return <span className="muted">не указан</span>
  return (
    <>
      <span className="registry-who__name">{debtor.short_name}</span>
      {debtor.more > 0 ? <More debtor={debtor} /> : null}
      {debtor.tag ? (
        <>
          {' '}
          <Tag kind="muted">{debtor.tag}</Tag>
        </>
      ) : null}
    </>
  )
}

function More({ debtor }) {
  const [open, setOpen] = useState(false)
  const [spot, setSpot] = useState(null)
  const anchorRef = useRef(null)
  const panelRef = useRef(null)
  usePanelSpot(open, anchorRef, panelRef, setSpot)

  const show = () => setOpen(true)
  const hide = () => {
    setOpen(false)
    setSpot(null)
  }

  return (
    <>
      {' '}
      {/*
        Кнопка, а не значок без роли, как у «i»: «и ещё 2» нажимают на телефоне,
        и читалке экрана нужно сказать, что это нажимается. `no-hover-lift` -
        для человека это ссылка в строке текста, подъём заставлял бы её прыгать
        под курсором (правило 6б). Панель лежит РЯДОМ с кнопкой, а не внутри:
        `transform` у предка сделал бы его точкой отсчёта для `position: fixed`.
      */}
      <button
        ref={anchorRef}
        type="button"
        className="registry-who__more no-hover-lift"
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={show}
        onKeyDown={(event) => {
          if (event.key === 'Escape') hide()
        }}
      >
        {debtor.more_title}
      </button>
      {open ? (
        <span
          ref={panelRef}
          role="tooltip"
          className="hint__panel registry-who__panel"
          style={spot ? { position: 'fixed', left: spot.left, top: spot.top } : MEASURING}
        >
          <span className="hint__title">{debtor.heading}</span>
          <ul className="registry-who__list">
            {debtor.lines.map((line, index) => (
              // Ключ - номер строки, и это допустимо по правилу 4 скилла
              // `frontend`: строка не уникальна (двое тёзок с равной долей),
              // своего ключа у неё нет, а перечень только на чтение и
              // меняется целиком.
              <li key={index}>{line}</li>
            ))}
          </ul>
        </span>
      ) : null}
    </>
  )
}

/*
  Положение панели по её НАСТОЯЩЕМУ размеру - тот же расчёт, что у значка «i»
  (`InfoHint.jsx`); при прокрутке и смене размера окна панель идёт следом.
*/
function usePanelSpot(open, anchorRef, panelRef, setSpot) {
  useLayoutEffect(() => {
    if (!open) return undefined
    const update = () => {
      const anchor = anchorRef.current
      const panel = panelRef.current
      if (!anchor || !panel) return
      const view = { width: window.innerWidth, height: window.innerHeight }
      setSpot(placeHint(anchor.getBoundingClientRect(), panel.getBoundingClientRect(), view))
    }
    update()
    // `capture`: прокручивается и рабочая зона кабинета, а её прокрутка до окна не всплывает.
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, anchorRef, panelRef, setSpot])
}
